import { useEffect, useMemo, useState } from 'react'

import { useBucketReportStore } from '../store'
import { collectLeafRows, extractBarChartData } from '../utils'

import DocumentsTable from './DocumentsTable'
import LineChart from './LineChart'
import { LineChartProvider } from './LineChart/context'

const BAR_METRICS = ['credits', 'cost']
const LINE_METRICS = ['balance', 'min', 'max', 'avg']
const LINE_LABELS = {
  balance: 'Balance',
  min: 'Min',
  max: 'Max',
  avg: 'Avg',
}
const LINE_COLORS = {
  balance: '#1f77b4',
  min: '#ff7f0e',
  max: '#2ca02c',
  avg: '#17becf',
}
const CHART_METRICS = [...BAR_METRICS, ...LINE_METRICS]

function ChartBody({ row, startDate, endDate }) {
  const {
    transactions: { list, documents, chartData: reportChartData },
  } = useBucketReportStore()
  const [chartData, setChartData] = useState({ data: [], keys: BAR_METRICS })
  const [lineMetric, setLineMetric] = useState(LINE_METRICS[0])
  const [sorting, setSorting] = useState([])
  const sortby = useMemo(() => {
    const [sort] = sorting

    if (!sort) return []

    return [`${sort.id} ${sort.desc ? 'desc' : 'asc'}`]
  }, [sorting])

  useEffect(() => {
    list({
      allocationBucketId: row.id,
      startDate,
      endDate,
      sortby,
    })
  }, [list, row.id, startDate, endDate, sortby])

  useEffect(() => {
    if (!collectLeafRows(reportChartData).length) {
      setChartData({ data: [], keys: BAR_METRICS })
      return
    }

    setChartData(extractBarChartData({ data: reportChartData, metrics: CHART_METRICS, endDate }))
  }, [reportChartData, endDate])

  const { barMinValue, barMaxValue, lineMinValue, lineMaxValue } = useMemo(() => {
    const getDomain = metrics => {
      const values = chartData.data.flatMap(datum => metrics.map(metric => Number(datum[metric]) || 0))

      if (!values.length) return { minValue: 0, maxValue: 1 }

      const minValue = Math.min(0, ...values)
      const maxValue = Math.max(0, ...values)

      if (minValue !== maxValue) return { minValue, maxValue }

      return { minValue: 0, maxValue: maxValue || 1 }
    }

    const barDomain = getDomain(BAR_METRICS)
    const lineDomain = getDomain([lineMetric])

    return {
      barMinValue: barDomain.minValue,
      barMaxValue: barDomain.maxValue,
      lineMinValue: lineDomain.minValue,
      lineMaxValue: lineDomain.maxValue,
    }
  }, [chartData, lineMetric])

  const hasChartData = !!chartData?.data?.length

  return (
    <div className="flex flex-col gap-6">
      <div style={{ height: 460 }}>
        {hasChartData ? <LineChart
            data={{ ...chartData, keys: BAR_METRICS }}
            lineMetric={lineMetric}
            lineColor={LINE_COLORS[lineMetric]}
            lineMetrics={LINE_METRICS}
            lineLabels={LINE_LABELS}
            lineColors={LINE_COLORS}
            onLineMetricChange={setLineMetric}
            barMinValue={barMinValue}
            barMaxValue={barMaxValue}
            lineMinValue={lineMinValue}
            lineMaxValue={lineMaxValue}
          /> : <div className="flex h-full items-center justify-center p-4 twy-text-base">No data to show.</div>}
      </div>
      <DocumentsTable data={documents} sorting={sorting} onSortingChange={setSorting} />
    </div>
  )
}

export default function ModalChartContent({ row, startDate, endDate }) {
  return (
    <LineChartProvider>
      <ChartBody row={row} startDate={startDate} endDate={endDate} />
    </LineChartProvider>
  )
}
